import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BodyReplaceDatasetApiV1DatasetDatasetNamePut } from '@rossum/mdh-api-client';
import { datasetApi, getDatasetRequestConfig } from '../api';
import { getOperationId } from '../getOperationId';
import { OPERATIONS_QUERY } from './useOperations';

export const DATASET_UPLOAD_MUTATION = [
  '@MDH',
  'replaceDatasetApiV2DatasetsDatasetNameDataPut',
] as const;

// Uploads a file into a dataset, replacing whatever data it has. Resolves with
// the import operation it started, when MDH says which one.
export const useDatasetUpload = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (
      payload: BodyReplaceDatasetApiV1DatasetDatasetNamePut & {
        datasetName: string;
      }
    ) => {
      const response =
        await datasetApi.replaceDatasetApiV2DatasetsDatasetNameDataPut(
          payload.datasetName,
          {
            encoding: payload.encoding,
            file: payload.file,
            dynamic: payload.dynamic,
          },
          getDatasetRequestConfig()
        );
      const operationId = getOperationId(response);
      // The import still runs, it just can't be followed.
      if (operationId === undefined) {
        window.Sentry?.captureMessage(
          'MDH upload response has no operation in its Location header',
          {
            level: 'warning',
            extra: {
              datasetName: payload.datasetName,
              location: response.headers['location'],
            },
          }
        );
      }
      return operationId;
    },
    {
      mutationKey: DATASET_UPLOAD_MUTATION,
      // Resolve once the operations list holds the upload, so it cannot be
      // started twice before the next poll.
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: OPERATIONS_QUERY }),
    }
  );
};
